import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from "react-select";
import { DateObject } from "react-multi-date-picker";
import axios from 'axios';
import break_box from '../../../../images/product/products.png';
import dayjs from 'dayjs';
import customParseFormat from "dayjs/plugin/customParseFormat";
import { toast } from 'react-toastify';

import LightGallery from 'lightgallery/react';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';

dayjs.extend(customParseFormat);

const EditProduct = ({ show, onClose, productId, productData, refresh }) => {

  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const user_id = parseInt(localStorage.getItem("user_id"), 10);
  const [serviceSttm, setServiceSttm] = useState(null);
  const [serviceEdtm, setServiceEdtm] = useState(null);
  const [packingSttm, setPackingSttm] = useState(null);
  const [packingEdtm, setPackingEdtm] = useState(null);
  const [deliverySttm, setDeliverySttm] = useState(null);
  const [deliveryEdtm, setDeliveryEdtm] = useState(null);
  const [optionsProduct, setOptionsProduct] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [optionsItemCategory, setOptionsItemCategory] = useState([]);
  const [description, setDescription] = useState('');
  const [productName, setProductName] = useState('');

  const parseTimeString = (timeString) => {
    if (!timeString) return null;

    const parsed = dayjs(timeString, "hh:mm A");
    if (!parsed.isValid()) return null;

    return new DateObject({
      hour: parsed.hour(),
      minute: parsed.minute(),
      second: 0,
      format: "HH:mm", // ensures 24-hour format
      calendar: "gregorian",
      locale: "en"
    });
  };

  useEffect(() => {
    const fetchProductCategories = async () => {
      try {
        const response = await axios.get(`${baseURL}/product-categories/products/active`);
        const categories = response.data.data;

        const formattedOptions = categories.map((category) => ({
          value: category.sno,
          label: category.productcategory_name,
          schedule_start_time: category.schedule_start_time,
          schedule_end_time: category.schedule_end_time,
        }));

        setOptionsProduct(formattedOptions);
      } catch (error) {
        console.error('❌ Error fetching product categories:', error);
      }
    };

    fetchProductCategories();
  }, []);

  const [itemCount, setItemCount] = useState('');
  const [itemCategory, setItemCategory] = useState([]);
  const [groupedSubCategories, setGroupedSubCategories] = useState({});

  useEffect(() => {
    const fetchGroupedCategories = async () => {
      try {
        const response = await axios.get(`${baseURL}/grouped-item-categories`);
        const data = response.data.data;

        const categoryOptions = data.map((cat) => ({
          value: cat.itemcategory_id,
          label: cat.itemcategory_name,
        }));
        setOptionsItemCategory(categoryOptions);

        const subcategoryMap = {};
        data.forEach((cat) => {
          subcategoryMap[cat.itemcategory_id] = cat.subcategories.map((sub) => ({
            value: sub.itemsubcategory_id,
            label: sub.itemsubcategory_name,
          }));
        });
        setGroupedSubCategories(subcategoryMap);
      } catch (error) {
        console.error('Error fetching categories with subcategories:', error);
      }
    };

    fetchGroupedCategories();
  }, []);

  useEffect(() => {
    // If productData is passed from the parent, use it directly
    if (productData) {
      setProductName(productData.product_name || '');
      setDescription(productData.description || '');
      setServiceSttm(parseTimeString(productData.production_start_time));
      setServiceEdtm(parseTimeString(productData.production_end_time));
      setPackingSttm(parseTimeString(productData.packing_start_time));
      setPackingEdtm(parseTimeString(productData.packing_end_time));
      setDeliverySttm(parseTimeString(productData.delivery_start_time));
      setDeliveryEdtm(parseTimeString(productData.delivery_end_time));
      setProductColor(productData.product_color || '#000000');
      setBrandImageFile(productData.brand_image || null);
      setFile(productData.product_image || null);

      setSelectedOption(
        optionsProduct.find(opt => opt.value === productData.product_category_id) || null
      );

      // ✅ Use improved mapping logic
      const rebuiltItemCategories = (productData.item_category_list || []).map((cat, index) => ({
        id: `item_${cat.item_category_id || index}_${Date.now()}`,
        category: cat.category
          ? cat.category // Already { value, label } if passed from parent
          : {
            value: cat.item_category_id,
            label: cat.item_category_name
          },
        count: cat.count || '1',
        subcategories: (cat.subcategories || []).map(sub => ({
          value: sub.value || sub.itemsubcategory_id,
          label: sub.label || sub.itemsubcategory_name
        }))
      }));

      const count = Number(productData?.item_count) || 1;
      if (rebuiltItemCategories.length) {
        setItemCategory(rebuiltItemCategories);
        setItemCount(rebuiltItemCategories.length);
      } else {
        const newCategories = Array.from({ length: count }, (_, i) => ({
          id: `item_${i}_${Date.now()}`,
          category: null,
          count: '1',
          subcategories: []
        }));

        setItemCategory(newCategories);
        setItemCount(count);
      }

      
      return; // Skip API fetch
    }

    // Otherwise, fetch from the API using productId
    const fetchProductDetails = async () => {
      try {
        const response = await axios.get(`${baseURL}/manages-products/${productId}`);
        const data = response.data.data;

        setProductName(data.product_name || '');
        setDescription(data.description || '');
        setServiceSttm(parseTimeString(data.production_start_time));
        setServiceEdtm(parseTimeString(data.production_end_time));
        setPackingSttm(parseTimeString(data.packing_start_time));
        setPackingEdtm(parseTimeString(data.packing_end_time));
        setDeliverySttm(parseTimeString(data.delivery_start_time));
        setDeliveryEdtm(parseTimeString(data.delivery_end_time));
        setProductColor(data.product_color || '#000000');
        setBrandImageFile(data.brand_image || null);
        setFile(data.product_image || null);

        setSelectedOption(
          optionsProduct.find(opt => opt.value === data.product_category_id) || null
        );

        // ✅ Use improved mapping logic for API data
        const rebuiltItemCategories = (data.item_category_list || []).map((cat, index) => ({
          id: `item_${cat.item_category_id || index}_${Date.now()}`,
          category: {
            value: cat.item_category_id,
            label: cat.item_category_name
          },
          count: cat.count || '1',
          subcategories: (cat.subcategories || []).map(sub => ({
            value: sub.itemsubcategory_id,
            label: sub.itemsubcategory_name
          }))
        }));


        const count = Number(productData?.item_count) || 1;

        if (rebuiltItemCategories.length) {
          setItemCategory(rebuiltItemCategories);
          setItemCount(rebuiltItemCategories.length);
        } else {
          const newCategories = Array.from({ length: count }, (_, i) => ({
            id: `item_${i}_${Date.now()}`,
            category: null,
            count: '1',
            subcategories: []
          }));

          setItemCategory(newCategories);
          setItemCount(count);
        }

      } catch (error) {
        console.error('❌ Error fetching product details', error);
      }
    };

    if (productId) {
      fetchProductDetails();
    }
  }, [productId, productData, optionsProduct, baseURL]);

  // Error states
  const [errors, setErrors] = useState({});
  const [itemcatErrors, setItemcatErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    const itemFieldErrors = {};

    // Force item_count to always match number of categories
    const validItemCount = itemCategory.length;
    if (validItemCount <= 0) {
      newErrors.item_count = 'At least one item category is required';
    }

    // Validate each category and subcategory
    const cleanedItemCategory = itemCategory.map((item, index) => {
      if (!item.category) {
        itemFieldErrors[`category_${index}`] = 'Category is required';
      }
      if (!item.subcategories || item.subcategories.length === 0) {
        itemFieldErrors[`subcategory_${index}`] = 'Select at least one subcategory';
      }
      return {
        ...item,
        count: item.count && !isNaN(item.count) ? String(item.count) : "1",
      };
    });

    // Set errors
    setItemcatErrors(itemFieldErrors);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0 || Object.keys(itemFieldErrors).length > 0) {
      return; // Stop if validation fails
    }

    // Prepare FormData
    const formData = new FormData();
    formData.append('updated_by', user_id);
    formData.append('product_name', productName);
    formData.append('product_category_id', selectedOption.value);
    formData.append('production_start_time', serviceSttm?.format?.('hh:mm A') || '');
    formData.append('production_end_time', serviceEdtm?.format?.('hh:mm A') || '');
    formData.append('packing_start_time', packingSttm?.format?.('hh:mm A') || '');
    formData.append('packing_end_time', packingEdtm?.format?.('hh:mm A') || '');
    formData.append('delivery_start_time', deliverySttm?.format?.('hh:mm A') || '');
    formData.append('delivery_end_time', deliveryEdtm?.format?.('hh:mm A') || '');
    formData.append('description', description || '');
    formData.append('product_color', productColor || '');
    formData.append('item_count', validItemCount); // Now always matches backend expectation

    if (file instanceof File) formData.append('product_image', file);
    if (brandImageFile instanceof File) formData.append('brand_image', brandImageFile);

    const item_category_list = cleanedItemCategory.map((item) => ({
      item_category_id: item.category?.value,
      count: item.count,
      subcategories: item.subcategories.map((sub) => ({
        itemsubcategory_id: sub.value,
        itemsubcategory_name: sub.label,
      })),
    }));

    formData.append('item_category_list', JSON.stringify(item_category_list));

    try {
      await axios.post(`${baseURL}/manages-products/${productId}`, formData);
      toast.success("Product updated successfully!");
      refresh();
      onClose();
    } catch (error) {
      console.error("Error updating product:", error?.response?.data || error.message);

      // Handle duplicate product error from backend (HTTP 409)
      if (error.response?.status === 409 && error.response.data?.message) {
        setErrors(prevErrors => ({
          ...prevErrors,
          product_name: error.response.data.message,  // Show inline error on product_name field
        }));
        return; // Do not show toast here
      }

      // Handle validation errors from backend (422)
      if (error.response?.status === 422 && error.response.data?.errors) {
        setErrors(error.response.data.errors);
        setItemcatErrors(error.response.data.errors); // or parse as needed
        return;
      }

      // Other errors - show toast
      toast.error(error.response?.data?.message || "Failed to update product.");
    }
  };

  const [productColor, setProductColor] = React.useState("#000000"); // default black or whatever

  const [file, setFile] = React.useState(null);
  const [brandImageFile, setBrandImageFile] = React.useState(null);

  return (
    <Modal show={show} size="lg">
      <div className="modal-content">
        <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
          <h3 className="modal-title text-white">Item Categories & Subcategories</h3>
          <Button variant="" type="button" className="close px-6 py-2" onClick={onClose}>
            <span className="text-white">×</span>
          </Button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>

            <div className="row">

              <div className="row mb-2">
                <div className="d-flex mb-2 align-items-center justify-content-start gap-3 mt-3">
                  <LightGallery speed={500} plugins={[lgThumbnail, lgZoom]}>
                    <div data-src={productData.product_image || break_box} className='border w-120px h-100px rounded px-1 py-0'>
                      <img src={productData.product_image || break_box} alt={productData.product_name} className='cursor-pointer w-125px h-100px' />
                    </div>
                  </LightGallery>
                  <div>
                    <label className='text-black font-w500 fs-5'>
                      <span>{productData.product_name}</span>
                      <span className='me-1 ms-1'>|</span>
                      <span>{productData.product_category_name}</span>
                    </label>
                  </div>
                </div>
              </div>


              {/* Number of Item Categories (auto counted) */}
              <div className="col-lg-4 mb-4 position-relative">
                <label className="text-black font-w500 fs-7">
                  Number of Item Categories <span className="required">*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="number"
                    className="form-control pe-5"
                    value={itemCategory.length}
                    readOnly
                  />
                  <button
                    type="button"
                    className="btn btn-success position-absolute top-50 translate-middle-y"
                    style={{ right: '8px', padding: '4px 10px', zIndex: 10 }}
                    onClick={() => {
                      const updated = [...itemCategory];
                      updated.push({
                        id: `item_${updated.length}_${Date.now()}`,
                        category: null,
                        count: '1', // default dummy count
                        subcategories: [],
                      });
                      setItemCategory(updated);
                    }}
                  >
                    <i className="fa fa-plus"></i>
                  </button>
                </div>
              </div>

              {/* Item Category Rows */}
              {itemCategory.length > 0 && (
                <div className="col-lg-12">
                  <label className="text-black font-w500 fs-7">
                    Item Categories & Subcategories <span className="required">*</span>
                  </label>
                  <div
                    style={{
                      maxHeight: '350px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      paddingRight: '10px',
                      border: '1px solid #dee2e6',
                      borderRadius: '5px',
                      padding: '10px',
                    }}
                  >
                    {itemCategory.map((item, index) => {
                      const availableCategoryOptions = optionsItemCategory.filter(
                        (option) =>
                          !itemCategory.some(
                            (selectedItem, selectedIndex) =>
                              selectedIndex !== index &&
                              selectedItem.category?.value === option.value
                          )
                      );

                      return (
                        <div className="row mb-3 align-items-end" key={item.id || index}>
                          {/* Category Select */}
                          <div className="col-lg-4">
                            <label className="text-black font-w500 fs-7">
                              Item Category #{index + 1} <span className="required">*</span>
                            </label>
                            <Select
                              placeholder={`Select Item Category #${index + 1}`}
                              value={item.category}
                              onChange={(selected) => {
                                const updated = [...itemCategory];
                                updated[index] = {
                                  ...updated[index],
                                  category: selected,
                                  subcategories: [],
                                };
                                setItemCategory(updated);
                              }}
                              options={availableCategoryOptions}
                              menuPlacement="top"
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                              // className={itemcatErrors[`category_${index}`] ? 'is-invalid' : ''}
                              className={itemcatErrors[`category_${index}`] ? 'is-invalid' : ''}
                            />

                            {itemcatErrors[`category_${index}`] && (
                              <span className="text-danger fs-8">
                                {itemcatErrors[`category_${index}`]}
                              </span>
                            )}
                          </div>

                          {/* Subcategory Multi-Select */}
                          <div className="col-lg-6">
                            <label className="text-black font-w500 fs-7">
                              Item Subcategories #{index + 1} <span className="required">*</span>
                            </label>
                            <Select
                              isMulti
                              placeholder="Select Subcategories"
                              value={item.subcategories || []}
                              onChange={(selectedOptions) => {
                                const updated = [...itemCategory];
                                updated[index] = {
                                  ...updated[index],
                                  subcategories: selectedOptions,
                                };
                                setItemCategory(updated);
                              }}
                              options={
                                item.category?.value
                                  ? groupedSubCategories[item.category.value] || []
                                  : []
                              }
                              menuPlacement="top"
                              menuPortalTarget={document.body}
                              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />

                            {itemcatErrors[`subcategory_${index}`] && (
                              <span className="text-danger fs-8">
                                {itemcatErrors[`subcategory_${index}`]}
                              </span>
                            )}
                          </div>

                          {/* Dummy Count */}
                          <div className="col-lg-2 d-none">
                            <label className="text-black font-w500 fs-7">
                              Count <span className="required">*</span>
                            </label>
                            <input
                              type="number"
                              min={1}
                              className="form-control"
                              value={item.count}
                              onChange={(e) => {
                                const updated = [...itemCategory];
                                updated[index].count = e.target.value;
                                setItemCategory(updated);
                              }}
                            />
                          </div>

                          {/* Delete Button */}
                          <div className="col-lg-2 text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => {
                                const updated = [...itemCategory];
                                updated.splice(index, 1);
                                setItemCategory(updated);
                              }}
                              disabled={itemCategory.length <= 1}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-4">
              <button type="submit" className="btn btn-sm btn-primary">Update Product <i className="fas fa-angle-double-right ms-2"></i></button>
            </div>

          </form>
        </div>
      </div>
    </Modal>
  );
};

export default EditProduct;
